import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { isUserTyping } from '@/lib/keyboardUtils';

interface UseKeyboardNavigationOptions {
  onEscape?: () => void;
  onBack?: () => void;
  enabled?: boolean;
}

/**
 * Hook for keyboard navigation shortcuts
 * - ESC: Navigate back or trigger custom handler
 * - Alt+Left (or Cmd+Left on Mac): Navigate back
 */
export const useKeyboardNavigation = (options: UseKeyboardNavigationOptions = {}) => {
  const navigate = useNavigate();
  const { onEscape, onBack, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcuts when user is typing in input fields
      if (isUserTyping(event)) return;

      // ESC key
      if (event.key === 'Escape') {
        if (onEscape) {
          onEscape();
        } else if (onBack) {
          onBack();
        } else {
          navigate(-1);
        }
        return;
      }

      // Alt+Left or Meta+Left (back navigation)
      if ((event.altKey || event.metaKey) && event.key === 'ArrowLeft') {
        event.preventDefault();
        if (onBack) {
          onBack();
        } else {
          navigate(-1);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, onEscape, onBack, enabled]);
};
